import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Save, X as XIcon,
} from 'lucide-react';
import adminService from '../../services/adminService';
import { refreshLevels } from '../../hooks/useLevels';
import { iconFor } from '../../utils/levels';
import Button from '../../components/ui/Button';
import './LevelManagement.css';

const BLANK = {
  name:       '',
  min_points: 0,
  max_points: '',
  color:      '#3b82f6',
  icon:       'trophy',
  blurb:      '',
};

const LevelManagement = () => {
  const navigate = useNavigate();
  const [levels, setLevels] = useState([]);
  const [allowedIcons, setAllowedIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | id | null
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const data = await adminService.getLevels();
      setLevels(data.levels || []);
      setAllowedIcons(data.allowed_icons || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load levels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    const nextSort = (levels.at(-1)?.sort_order || 0) + 1;
    const nextMin  = levels.at(-1)?.max_points ?? 0;
    setEditing('new');
    setForm({ ...BLANK, sort_order: nextSort, min_points: nextMin });
    setError(null);
  };

  const startEdit = (lvl) => {
    setEditing(lvl.id);
    setForm({
      name:       lvl.name,
      min_points: lvl.min_points,
      max_points: lvl.max_points ?? '',
      color:      lvl.color,
      icon:       lvl.icon,
      blurb:      lvl.blurb || '',
      sort_order: lvl.sort_order,
    });
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setForm(BLANK);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        min_points: Number(form.min_points),
        max_points: form.max_points === '' || form.max_points == null ? null : Number(form.max_points),
      };
      if (editing === 'new') {
        await adminService.createLevel(payload);
      } else {
        await adminService.updateLevel(editing, payload);
      }
      await load();
      refreshLevels();
      cancel();
    } catch (e) {
      const msg = e.response?.data?.message
        || Object.values(e.response?.data?.errors || {}).flat().join('  ')
        || 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lvl) => {
    if (!window.confirm(`Delete level "${lvl.name}"? Players in this tier will be re-bucketed instantly.`)) return;
    try {
      await adminService.deleteLevel(lvl.id);
      await load();
      refreshLevels();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const move = async (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= levels.length) return;
    const next = [...levels];
    [next[idx], next[target]] = [next[target], next[idx]];
    const order = next.map((l, i) => ({ id: l.id, sort_order: i + 1 }));
    setLevels(next.map((l, i) => ({ ...l, sort_order: i + 1 })));
    try {
      await adminService.reorderLevels(order);
      refreshLevels();
    } catch (e) {
      alert('Reorder failed');
      load();
    }
  };

  const formatRange = (lvl) =>
    lvl.max_points == null
      ? `${lvl.min_points.toLocaleString()}+ pts`
      : `${lvl.min_points.toLocaleString()} – ${lvl.max_points.toLocaleString()} pts`;

  if (loading) {
    return <div className="lvm container"><p>Loading levels…</p></div>;
  }

  const editor = (
    <div className="lvm-editor">
      <h3>{editing === 'new' ? 'New level' : 'Edit level'}</h3>
      {error && <div className="lvm-error">{error}</div>}
      <div className="lvm-grid">
        <label className="lvm-field">
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            maxLength={60}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Rising"
          />
        </label>

        <label className="lvm-field">
          <span>Color</span>
          <div className="lvm-color-row">
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="#3b82f6"
            />
          </div>
        </label>

        <label className="lvm-field">
          <span>Min points</span>
          <input
            type="number"
            min="0"
            value={form.min_points}
            onChange={(e) => setForm({ ...form, min_points: e.target.value })}
          />
        </label>

        <label className="lvm-field">
          <span>Max points <em>(empty = top tier, open-ended)</em></span>
          <input
            type="number"
            min="0"
            value={form.max_points}
            onChange={(e) => setForm({ ...form, max_points: e.target.value })}
            placeholder="leave empty for top tier"
          />
        </label>

        <div className="lvm-field lvm-field-icons">
          <span>Icon</span>
          <div className="lvm-icon-grid">
            {allowedIcons.map((key) => {
              const Icon = iconFor(key);
              const selected = form.icon === key;
              return (
                <button
                  type="button"
                  key={key}
                  className={`lvm-icon-btn${selected ? ' is-selected' : ''}`}
                  onClick={() => setForm({ ...form, icon: key })}
                  style={selected ? { borderColor: form.color, color: form.color, background: `${form.color}1a` } : undefined}
                  aria-label={key}
                  aria-pressed={selected}
                >
                  <Icon size={18} />
                </button>
              );
            })}
          </div>
        </div>

        <label className="lvm-field lvm-field-wide">
          <span>Blurb <em>(optional, shown in the dialog)</em></span>
          <input
            type="text"
            maxLength={160}
            value={form.blurb}
            onChange={(e) => setForm({ ...form, blurb: e.target.value })}
            placeholder="e.g. Patterns clicking — moving up the ladder."
          />
        </label>
      </div>

      <div className="lvm-actions">
        <Button onClick={cancel} variant="secondary" disabled={saving}>
          <XIcon size={14} /> Cancel
        </Button>
        <Button onClick={handleSave} variant="primary" disabled={saving || !form.name}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save level'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="lvm container">
      <header className="lvm-header">
        <div>
          <h1>Level Tiers</h1>
          <p className="lvm-sub">Ranges are computed from each player's <strong>total_score</strong>. Changes apply instantly to Profile, Dashboard and Leaderboard.</p>
        </div>
        <div className="lvm-header-actions">
          <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">Back</Button>
          <Button onClick={startNew} variant="primary" disabled={editing !== null}>
            <Plus size={14} /> New level
          </Button>
        </div>
      </header>

      {editing === 'new' && editor}

      <div className="lvm-list">
        {levels.length === 0 && <p className="lvm-empty">No levels yet. Create one to get started.</p>}
        {levels.map((lvl, idx) => {
          const Icon = iconFor(lvl.icon);
          const isEditing = editing === lvl.id;
          return (
            <div key={lvl.id} className={`lvm-row${isEditing ? ' is-editing' : ''}`}>
              <div className="lvm-row-main">
                <div
                  className="lvm-row-icon"
                  style={{ color: lvl.color, background: `${lvl.color}1a`, borderColor: `${lvl.color}55` }}
                >
                  <Icon size={20} />
                </div>
                <div className="lvm-row-info">
                  <span className="lvm-row-name" style={{ color: lvl.color }}>{lvl.name}</span>
                  <span className="lvm-row-range">{formatRange(lvl)}</span>
                  {lvl.blurb && <span className="lvm-row-blurb">{lvl.blurb}</span>}
                </div>
                <div className="lvm-row-actions">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up" className="lvm-icon-action">
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === levels.length - 1} aria-label="Move down" className="lvm-icon-action">
                    <ArrowDown size={14} />
                  </button>
                  <button type="button" onClick={() => startEdit(lvl)} aria-label="Edit" className="lvm-icon-action" disabled={editing !== null}>
                    <Pencil size={14} />
                  </button>
                  <button type="button" onClick={() => handleDelete(lvl)} aria-label="Delete" className="lvm-icon-action lvm-danger" disabled={editing !== null}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {isEditing && editor}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LevelManagement;
